import { List, useTable, ShowButton, EditButton } from '@refinedev/antd'
import { Table, Space, Avatar, Tag, Input, Button, Typography, Card, Row, Col, Statistic, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import {
  SearchOutlined,
  MailOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  TeamOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import type { BaseRecord } from '@refinedev/core'

const { Text } = Typography

interface Doctor extends BaseRecord {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  phone?: string | null
  image?: string | null
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED'
  createdAt: string
  updatedAt: string
  patientCount: number
  doctor?: {
    id: string
    title?: string | null
    specialty: string
    licenseNumber: string
    availability?: unknown
    dailyRoomName?: string | null
    _count?: {
      appointments: number
    }
  } | null
}

const statusConfig: Record<Doctor['status'], { color: string; text: string }> = {
  ACTIVE: { color: 'success', text: 'Aktivan' },
  SUSPENDED: { color: 'warning', text: 'Suspendovan' },
  DELETED: { color: 'default', text: 'Obrisan' },
}

export function DoctorsListPage() {
  const { tableProps, searchFormProps } = useTable<Doctor>({
    resource: 'doctors',
    syncWithLocation: true,
    onSearch: (values) => {
      const searchValues = values as { search?: string }
      return [
        {
          field: 'search',
          operator: 'contains',
          value: searchValues.search,
        },
      ]
    },
  })

  const getDropdownItems = (record: Doctor): MenuProps['items'] => [
    {
      key: 'show',
      label: <Link to={`/doctors/${record.id}`}>Prikaži</Link>,
    },
    {
      key: 'edit',
      label: <Link to={`/doctors/${record.id}/edit`}>Izmeni</Link>,
    },
    {
      key: 'patients',
      label: <Link to={`/doctors/${record.id}/patients`}>Pacijenti</Link>,
    },
  ]

  return (
    <List
      title="Lekari"
      headerButtons={
        <Link to="/doctors/invite">
          <Button type="primary" icon={<MailOutlined />}>
            Pozovi lekara
          </Button>
        </Link>
      }
    >
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Ukupno lekara"
              value={tableProps.pagination && typeof tableProps.pagination === 'object' ? tableProps.pagination.total : 0}
              prefix={<MedicineBoxOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Aktivnih"
              value={tableProps.dataSource?.filter(d => d.status === 'ACTIVE').length || 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Ukupno pacijenata"
              value={tableProps.dataSource?.reduce((acc, d) => acc + (d.patientCount || 0), 0) || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Search */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <form {...searchFormProps}>
          <Space>
            <Input
              placeholder="Pretraži po imenu ili emailu..."
              prefix={<SearchOutlined />}
              style={{ width: 300 }}
              name="search"
              allowClear
            />
            <Button type="primary" htmlType="submit">
              Pretraži
            </Button>
          </Space>
        </form>
      </Card>

      {/* Table */}
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 800 }}
      >
        <Table.Column
          dataIndex="firstName"
          title="Lekar"
          render={(_, record: Doctor) => (
            <Space>
              <Avatar
                src={record.image}
                icon={!record.image && <UserOutlined />}
                style={{ backgroundColor: '#1677ff' }}
              >
                {!record.image && `${record.firstName?.[0] || 'D'}${record.lastName?.[0] || ''}`}
              </Avatar>
              <Link to={`/doctors/${record.id}`}>
                <Text strong style={{ color: '#1677ff', cursor: 'pointer' }}>
                  {record.doctor?.title || 'Dr.'} {record.firstName || ''} {record.lastName || ''}
                </Text>
              </Link>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="email"
          title="Email"
          render={(email: string) => <Text>{email}</Text>}
        />
        <Table.Column
          dataIndex={['doctor', 'specialty']}
          title="Specijalizacija"
          render={(specialty: string) => specialty || 'Opšta praksa'}
        />
        <Table.Column
          dataIndex="patientCount"
          title="Pacijenata"
          render={(count: number) => count || 0}
          sorter
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: Doctor['status']) => {
            const config = statusConfig[status]
            return <Tag color={config.color}>{config.text}</Tag>
          }}
          filters={[
            { text: 'Aktivan', value: 'ACTIVE' },
            { text: 'Suspendovan', value: 'SUSPENDED' },
            { text: 'Obrisan', value: 'DELETED' },
          ]}
        />
        <Table.Column
          title="Akcije"
          fixed="right"
          width={100}
          render={(_, record: Doctor) => (
            <Space size="small">
              <ShowButton hideText size="small" recordItemId={record.id} />
              <EditButton hideText size="small" recordItemId={record.id} />
              <Dropdown menu={{ items: getDropdownItems(record) }} trigger={['click']}>
                <Button size="small" icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          )}
        />
      </Table>
    </List>
  )
}

export { default as DoctorInvitePage } from './invite'
